import React from "react";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { CustomChip, ReuseInput, ReuseTooltip } from "../../../../ui";
import { DeleteIcon } from "../../../../images";
import { CustomButton } from "../../../../ui/CustomButton";
import { useState } from "react";
import { CIDAddedType } from "../../../../../types/apiTypes";
import { JobTitlesType } from "../../../../../types/enums";
import { KeyTitleAddItemBlock } from "../../../KeyTitles/KeyTitleAddItemBlock";
import { toast } from "react-toastify";
import { CustomToast } from "../../../../ui/CustomToast";
import CancelIcon from "@mui/icons-material/Cancel";

type Props = {
  isLoading: boolean;
  cids: CIDAddedType[];
  setCids: React.Dispatch<React.SetStateAction<CIDAddedType[]>>;
};

export const UploadCidsMethod = ({ cids, setCids, isLoading }: Props) => {
  const [cidName, setCidName] = useState<string>("");

  const addCidHandler = () => {
    if (cidName.length < 3) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid CID"
          text="The CID must contain at least 3 characters."
        />,
      );
      return;
    }

    setCids((prev) => {
      if (prev.some(({ cid }) => cid === cidName)) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid CID"
            text="Each job CID must be unique. Please choose a different CID."
          />,
        );

        return prev;
      }
      setCidName("");
      return [...prev, { cid: cidName, isAdded: true, sub_cids: [] }];
    });
  };

  const addSubCidHandler = (
    subSid: string,
    cid: string,
    onSuccess: () => void,
  ) => {
    if (isLoading) return;

    if (subSid.length < 3) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Sub-CID"
          text="The Sub-CID must contain at least 3 characters."
        />,
      );
      return;
    }

    setCids((prev) => {
      const state = [...prev];
      const currentCid = state.findIndex((item) => item.cid === cid);
      if (currentCid === -1) return prev;

      if (state[currentCid].sub_cids.some(({ name }) => name === subSid)) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid SubCID"
            text="Each job SubCID must be unique. Please choose a different SubCID."
          />,
        );

        return prev;
      }

      onSuccess();

      return state.map((item, idx) => {
        if (currentCid === idx) {
          return {
            ...item,
            sub_cids: [...item.sub_cids, { name: subSid, isAdded: true }],
          };
        }
        return item;
      });
    });
  };
  const removeCidHandler = (cid: string) => {
    setCids((prev) => prev.filter((item) => item.cid !== cid));
  };
  const removeSubCidHandler = (cid: string, subSid: string) => {
    setCids((prev) => {
      const state = [...prev];
      const currentCid = state.findIndex((item) => item.cid === cid);
      if (currentCid === -1) return prev;

      state[currentCid].sub_cids = state[currentCid].sub_cids.filter(
        ({ name }) => name !== subSid,
      );
      return state;
    });
  };

  return (
    <Stack paddingTop={"16px"} paddingBottom={"16px"} gap={"32px"}>
      <Stack gap={"8px"} flexDirection={"row"} alignItems={"center"}>
        <ReuseInput
          isDisabled={isLoading}
          name="cid"
          handleInputChange={(field, value) => setCidName(value)}
          errorMessage={""}
          value={cidName}
          label="CID"
          placeholder="AA12345"
          isError={false}
          setErrorHandler={() => {}}
        />
        <CustomButton
          onClick={addCidHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          customSize="fit"
          disabled={!cidName || isLoading}
          sx={{
            height: "40px",
            borderColor: "#1D3E70",
            padding: "8px 16px",

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              borderColor: "#EEEEEE",
            },
          }}
        >
          Add CID
        </CustomButton>
      </Stack>
      <Stack gap={"12px"}>
        {cids.map(({ cid, isAdded, sub_cids }, index) => (
          <React.Fragment key={cid}>
            <Stack gap={"16px"} flexDirection={"row"} alignItems={"center"}>
              <Typography
                variant="body1"
                color="custom.textDarkBlue"
                sx={{
                  color: "#464646",
                  maxWidth: "78px",
                  width: "100%",
                }}
              >
                CID {index + 1}
              </Typography>
              <CustomChip
                label={cid}
                onDelete={isAdded ? () => {} : undefined}
                disabled={isLoading}
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid #BCBCBC",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 500,
                  color: "#232323",
                }}
                deleteIcon={
                  <ReuseTooltip
                    text={"Removing this CID will also remove all Sub-CIDs."}
                    maxWidth={"200px"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <CancelIcon
                      sx={{
                        color: "#617798",
                        width: "20px",
                        height: "20px",
                        marginRight: "6px",
                      }}
                      onClick={() => removeCidHandler(cid)}
                    />
                  </ReuseTooltip>
                }
              />
            </Stack>
            {(sub_cids.length > 0 || isAdded) && (
              <Stack gap={"16px"} flexDirection={"row"} alignItems={"center"}>
                <Typography
                  variant="body1"
                  color="custom.textDarkBlue"
                  sx={{
                    color: "#464646",
                    maxWidth: "78px",
                    width: "100%",
                  }}
                >
                  Sub-CID(s)
                </Typography>
                <Stack flexDirection={"row"} gap={"8px"} flexWrap={"wrap"}>
                  {sub_cids.map(({ name, isAdded }) => (
                    <Stack key={name} flexDirection="row" gap="4px">
                      <Stack
                        sx={{
                          padding: "4px",
                          borderRadius: "6px",
                          backgroundColor: "#F4F5F7",
                          flexDirection: "row",
                          alignItems: "center",
                          flex: 1,
                          border: `1px solid "#DEDEDE"`,
                        }}
                      >
                        <Stack
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "4px",
                            paddingLeft: "4px",
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body1"
                            color="custom.textDarkBlue"
                            sx={{
                              whiteSpace: "nowrap",
                              fontWeight: 500,
                            }}
                          >
                            {name}
                          </Typography>
                        </Stack>

                        <Stack
                          flexDirection="row"
                          flexWrap="nowrap"
                          paddingLeft="4px"
                        >
                          {isAdded && (
                            <ReuseTooltip text="Delete SubCid">
                              <IconButton
                                sx={{
                                  height: "24px",
                                  width: "24px",
                                }}
                                disabled={isLoading}
                                onClick={() => removeSubCidHandler(cid, name)}
                              >
                                <DeleteIcon
                                  height={"16px"}
                                  width={"16px"}
                                  color={"#9B9B9B"}
                                />
                              </IconButton>
                            </ReuseTooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  ))}
                  <KeyTitleAddItemBlock
                    isDisabled={isLoading}
                    titleType={JobTitlesType.approved}
                    roleId={cid}
                    isReachedLimit={true}
                    customAddHandler={addSubCidHandler}
                    btnText={"Add Sub-CID"}
                    placeholder={"Enter Sub-CID"}
                  />
                </Stack>
              </Stack>
            )}

            {index !== cids.length - 1 && <Divider sx={{ width: "100%" }} />}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
};
